import React from 'react'
import {
  UserSurveyPrimaryFunction,
  UserSurveyRole,
} from 'Generated/graphql-types'
import { useAppState } from 'Context/AppContext/AppContext'
import { ActionElementType } from 'Components/SharedComponents/Surveys/constants'
import {
  StepContainer,
  Title,
} from 'Components/SharedComponents/Surveys/Surveys.styled'
import { NextActions } from 'Components/SharedComponents/Surveys/NextActions/NextActions'
import { UserActions } from 'Components/SharedComponents/Surveys/UserActions/UserActions'
import {
  ButtonAction,
  CustomAction,
} from 'Components/SharedComponents/Surveys/types'
import { HandleAnyAction } from '../../types'
import { NeedsAssessmentWizardStep } from '../../constants'
import { sendStepTransitionEvent } from '../../utils'

type BaselineConfidenceProps = {
  baselineConfidence?: string
  onAnyAction: HandleAnyAction
  onSetBaselineConfidence: (confidenceLevel: string) => void
  userJourneyLength: number
  userPrimaryFunction: UserSurveyPrimaryFunction | undefined
  userRole?: UserSurveyRole
}

export const BaselineConfidence = ({
  baselineConfidence,
  onAnyAction,
  onSetBaselineConfidence,
  userJourneyLength,
  userPrimaryFunction,
  userRole,
}: BaselineConfidenceProps) => {
  const { nonprofitOrgConfig, userConfig } = useAppState()
  const userActions: CustomAction[] = [
    {
      callback: () => onSetBaselineConfidence('1'),
      checkValue: baselineConfidence,
      label: '1: Very low: Little to no confidence',
      type: ActionElementType.RADIO,
      value: '1',
    },
    {
      callback: () => onSetBaselineConfidence('2'),
      checkValue: baselineConfidence,
      label: '2: Low: Limited confidence',
      type: ActionElementType.RADIO,
      value: '2',
    },
    {
      callback: () => onSetBaselineConfidence('3'),
      checkValue: baselineConfidence,
      label: '3: Moderate: Some confidence',
      type: ActionElementType.RADIO,
      value: '3',
    },
    {
      callback: () => onSetBaselineConfidence('4'),
      checkValue: baselineConfidence,
      label: '4: High: Strong confidence',
      type: ActionElementType.RADIO,
      value: '4',
    },
    {
      callback: () => onSetBaselineConfidence('5'),
      checkValue: baselineConfidence,
      label: '5: Very high: Extremely confident',
      type: ActionElementType.RADIO,
      value: '5',
    },
    {
      callback: () => onSetBaselineConfidence('0'),
      checkValue: baselineConfidence,
      label: '0: Unsure',
      type: ActionElementType.RADIO,
      value: '0',
    },
  ]

  const nextStep = NeedsAssessmentWizardStep.BASELINE_SUPPORT_NEEDED
  const nextActions: ButtonAction[] = [
    {
      disabled: !baselineConfidence,
      label: 'Next',
      path: nextStep,
      saveUserState: true,
      type: ActionElementType.BUTTON,
      callback: () => {
        sendStepTransitionEvent({
          currentStep: NeedsAssessmentWizardStep.BASELINE_CONFIDENCE,
          nextStep,
          userId: userConfig.id,
          nonprofitOrgId: nonprofitOrgConfig.orgId,
          funderOrgId: nonprofitOrgConfig.funderOrgId,
          userEmail: userConfig.email,
          userPrimaryFunction,
          userJourneyLength,
          userRole,
        })
      },
    },
  ]

  return (
    <StepContainer>
      <Title>
        How confident do you feel right now that your organization is making
        progress towards achieving its stated mission?
      </Title>
      <UserActions onAnyAction={onAnyAction} userActions={userActions} />
      <NextActions nextActions={nextActions} onAnyAction={onAnyAction} />
    </StepContainer>
  )
}
